import axios from "axios";
import Cookies from "js-cookie";
import { ENCRYPTION_OF_DATA } from "helper/Functions";
import { API_URL } from "helper/Constants";

class PaymentMethodService {
  constructor(baseURL) {
    this.api = axios.create({
      baseURL: baseURL ? baseURL : API_URL,
    });
    this.lastModified = null;

    this.api.interceptors.request.use(
      async (config) => {
        const token = Cookies.get("token");
        if (token) {
          config.headers["Authorization"] = `Bearer ${token}`;
        }
        if (config.data) {
          config.headers["Content-Type"] = `application/json`;
          config.data = await ENCRYPTION_OF_DATA(config.data);
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response && (error.response.status === 401 && error.response.data.message !== "Invalid refresh token")) {
          try {
            const newToken = await this.refreshToken();
            if (newToken) {
              originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
              return axios(originalRequest);
            }
          } catch (refreshError) {
            window?.location?.reload()
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  async refreshToken() {
    try {
      const response = await this.api.post(
        "/auth/refresh-token",
        {},
        {
          withCredentials: true,
        }
      );
      const newAccessToken = response.data.accessToken;
      Cookies.set("token", newAccessToken, {
        secure: true,
        sameSite: "None",
      });
      return newAccessToken;
    } catch (error) {
      Cookies.remove("user", { secure: true, sameSite: "None" });
      Cookies.remove("token", { secure: true, sameSite: "None" });
      Cookies.remove("refreshToken", { secure: true, sameSite: "None" });
      window?.location?.reload()
      console.error("Failed to refresh token:", error);
      return null;
    }
  }

  async fetchPaymentMethods({ type, activeOnly = true, page_number = 1, items_per_page = 50 }) {
    const token = Cookies.get("token");

    if (!token) {
      throw new Error("User is not authenticated. Token is missing.");
    }

    try {
      const params = {
        page_number,
        items_per_page,
      };

      if (type) params.type = type;
      if (activeOnly !== undefined) params.activeOnly = activeOnly;

      const response = await this.api.get(`/api/payment-methods`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        return response.data.data || [];
      } else {
        throw new Error(response.data.message || "Failed to fetch payment methods.");
      }
    } catch (error) {
      console.error("Error fetching payment methods:", error);
      throw error;
    }
  }

  async fetchPaymentMethodById(id) {
    const token = Cookies.get("token");

    if (!token) {
      throw new Error("User is not authenticated. Token is missing.");
    }

    if (!id) {
      throw new Error("Payment method ID is required");
    }

    try {
      const response = await this.api.get(`/api/payment-methods/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || "Failed to fetch payment method.");
      }
    } catch (error) {
      console.error("Error fetching payment method:", error);
      throw error;
    }
  }

  async createPaymentMethod(paymentMethodData) {
    const token = Cookies.get("token");

    if (!token) {
      throw new Error("User is not authenticated. Token is missing.");
    }

    const { name, type, logo, description, instructions, accountDetails, order, isActive } = paymentMethodData;

    if (!name || !type || !logo || !description) {
      throw new Error("Name, type, logo, and description are required");
    }

    try {
      const response = await this.api.post(
        `/api/payment-methods`,
        {
          name,
          type,
          logo,
          description,
          instructions,
          accountDetails: accountDetails || {},
          order: order || 0,
          isActive: isActive !== undefined ? isActive : true
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || "Failed to create payment method.");
      }
    } catch (error) {
      console.error("Error creating payment method:", error);
      throw error;
    }
  }

  async updatePaymentMethod(id, updateData) {
    const token = Cookies.get("token");

    if (!token) {
      throw new Error("User is not authenticated. Token is missing.");
    }

    if (!id) {
      throw new Error("Payment method ID is required");
    }

    try {
      const response = await this.api.put(
        `/api/payment-methods/${id}`,
        updateData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || "Failed to update payment method.");
      }
    } catch (error) {
      console.error("Error updating payment method:", error);
      throw error;
    }
  }

  async deletePaymentMethod(id) {
    const token = Cookies.get("token");

    if (!token) {
      throw new Error("User is not authenticated. Token is missing.");
    }

    if (!id) {
      throw new Error("Payment method ID is required");
    }

    try {
      const response = await this.api.delete(`/api/payment-methods/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || "Failed to delete payment method.");
      }
    } catch (error) {
      console.error("Error deleting payment method:", error);
      throw error;
    }
  }

  async togglePaymentMethodStatus(id) {
    const token = Cookies.get("token");

    if (!token) {
      throw new Error("User is not authenticated. Token is missing.");
    }

    if (!id) {
      throw new Error("Payment method ID is required");
    }

    try {
      const response = await this.api.patch(
        `/api/payment-methods/${id}/toggle`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || "Failed to toggle payment method status.");
      }
    } catch (error) {
      console.error("Error toggling payment method status:", error);
      throw error;
    }
  }

  async fetchActivePaymentMethods(type = null) {
    const filters = { activeOnly: true };
    if (type) filters.type = type;
    return await this.fetchPaymentMethods(filters);
  }

  async fetchDepositMethods(activeOnly = true) {
    return await this.fetchPaymentMethods({ type: 'deposit', activeOnly });
  }

  async fetchWithdrawalMethods(activeOnly = true) {
    return await this.fetchPaymentMethods({ type: 'withdrawal', activeOnly });
  }

  async fetchBothMethods(activeOnly = true) {
    return await this.fetchPaymentMethods({ type: 'both', activeOnly });
  }
}

export default PaymentMethodService;